import { FC, ReactElement } from 'react';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';

import { PAGE_ID_BY_CHANNEL_OFFER_GROUP, PLANS_TEST_ID } from '../../../constants';
import { ServiceOfferItem } from '../../../model';
import { GiftPreviewButton, PlanCard, PlanCardModel } from '../../PlanCard';
import {
    DownloadUniqueOfferContractSummaryButton,
    DownloadUniqueOfferWithMsisdnContractSummaryButton,
} from '../../PlanCard/public-components/DownloadContractSummaryButton';
import { usePlanOrderingFlow } from '../../PlanCard/public-components/PlanOrdering/usePlanOrderingFlow';
import { transformServiceOffersToPlanCardDescriptions, transformServiceOffersToPlanCardDetails } from './PlanCardUtils';

type Props = {
    serviceOffer: ServiceOfferItem;
    msisdn?: string;
    index?: number;
    onSelectItem?: (service: ServiceOfferItem, index: number) => void;
    isVASExpanded?: boolean;
    onVASExpandChange?: (isExpanded: boolean) => void;
    ctaLoading?: boolean;
    ctaDisabled?: boolean;
};

export const ServiceOfferPlanCard: FC<Props> = ({
    serviceOffer,
    msisdn,
    index,
    onSelectItem,
    isVASExpanded,
    onVASExpandChange,
    ctaLoading,
    ctaDisabled,
}) => {
    const { t } = useTranslations();
    const uidForModal = serviceOffer.id;

    const cap: PlanCardModel.CapProps = serviceOffer.cap
        ? {
              text: serviceOffer.cap.name,
              variant: serviceOffer.cap.colorOption,
              icon: serviceOffer.cap.icon,
              image: serviceOffer.cap.image,
          }
        : null;

    const badge: PlanCardModel.BadgeProps = serviceOffer.badge
        ? {
              text: serviceOffer.badge.name,
              variant: serviceOffer.badge.colorOption === 'gray' ? 'neutral' : 'attention',
          }
        : undefined;

    const header = {
        title: serviceOffer.isDataSizeDomesticTotalUnlimited
            ? t['plans.data.unlimited']
            : serviceOffer.dataSizeDomesticTotalFormatted,
        oldTitle: serviceOffer.dataSizeDomesticStandardFormatted,
        subtitle: serviceOffer.name,
        badge,
    };

    const details = transformServiceOffersToPlanCardDetails([serviceOffer]);

    const isCampaignAvailable = !!serviceOffer.campaign?.name && !!serviceOffer.campaign?.icon;
    const getCampaignButton = (negative: boolean): ReactElement | null =>
        isCampaignAvailable ? (
            <GiftPreviewButton
                uidForGiftPreviewModal={uidForModal}
                isNegative={negative}
                icon={serviceOffer.campaign.icon}
                data-test={PLANS_TEST_ID.CAMPAIGN_BUTTON}
            >
                {serviceOffer.campaign.name}
            </GiftPreviewButton>
        ) : null;

    const ContractSummaryButton: PlanCardModel.PassableComponent = !!msisdn
        ? ({ isNegative: negative }) => (
              <DownloadUniqueOfferWithMsisdnContractSummaryButton
                  params={{
                      code: PAGE_ID_BY_CHANNEL_OFFER_GROUP.SelfService[serviceOffer.offerGroup],
                      uniqueOfferId: serviceOffer.uniqueOfferId,
                      promoOfferId: serviceOffer.teliaOneOfferId,
                      msisdn,
                  }}
                  isNegative={negative}
              />
          )
        : ({ isNegative: negative }) => (
              <DownloadUniqueOfferContractSummaryButton
                  params={{
                      code: PAGE_ID_BY_CHANNEL_OFFER_GROUP.PublicWeb[serviceOffer.offerGroup],
                      uniqueOfferId: serviceOffer.uniqueOfferId,
                      promoOfferId: serviceOffer.teliaOneOfferId,
                  }}
                  isNegative={negative}
              />
          );

    const { startPlanOrderFlow } = usePlanOrderingFlow();
    const ctaText =
        serviceOffer.orderActionType === 'LEAD'
            ? t['plans.card.call_to_action.lead']
            : t['plans.card.call_to_action.checkout'];

    const body = {
        details,
        campaign: getCampaignButton,
        oldPrice: serviceOffer.priceStandardFormatted,
        price: serviceOffer.priceFormatted,
        contractSummary: ContractSummaryButton,
        callToAction: {
            text: ctaText,
            loading: ctaLoading,
            disabled: ctaDisabled,
            onClick: (event) => {
                onSelectItem?.(serviceOffer, index);
                startPlanOrderFlow(uidForModal, event.currentTarget);
            },
        },
    };

    const descriptions = transformServiceOffersToPlanCardDescriptions([serviceOffer]);

    const footer = {
        title: t['plans.card.footer.title'],
        descriptions,
        isForcedExpanded: isVASExpanded,
        onExpandChange: onVASExpandChange,
    };

    const isNegative = serviceOffer.colorOption === 'highlighted';

    return <PlanCard cap={cap} header={header} body={body} footer={footer} isNegative={isNegative} />;
};
